import * as React from 'react';
import Head from 'next/head';
import type { Graph, WithContext } from 'schema-dts';

/**
 * Renders structured data (JSON-LD) schema for SEO purposes.
 *
 * This component takes structured data conforming to schema.org standards and
 * renders it as a JSON-LD script tag in the document head. This helps search
 * engines understand the content and context of the page.
 *
 * @param schemaData - A structured data object that conforms to schema.org standards.
 *   Must include a `@context` property (typically "https://schema.org") and either:
 *   - A `@type` property indicating the schema type (e.g., "Article", "Organization", "Product")
 *   - A `@graph` property containing an array of multiple schema objects
 *   The object should be typed with `WithContext<T>` where T is the specific schema type.
 *
 * @example
 * Single schema object:
 * ```tsx
 * const articleSchema: WithContext<BlogPosting> = {
 *   '@context': 'https://schema.org',
 *   '@type': 'BlogPosting',
 *   headline: 'Article Title',
 *   author: { '@type': 'Person', name: 'Author Name' },
 *   datePublished: '2024-01-01'
 * };
 *
 * <RenderSEOSchema schemaData={articleSchema} />
 * ```
 *
 * @example
 * Multiple schemas using @graph:
 * ```tsx
 * const schemaWithGraph = {
 *   '@context': 'https://schema.org',
 *   '@graph': [
 *     { '@type': 'WebPage', name: 'Page Title' },
 *     { '@type': 'Organization', name: 'Company Name' },
 *     { '@type': 'WebSite', name: 'Website Name' }
 *   ]
 * };
 *
 * <RenderSEOSchema schemaData={schemaWithGraph} />
 * ```
 *
 * @see https://schema.org/ for available schema types
 * @see https://developers.google.com/search/docs/appearance/structured-data for Google's structured data guidelines
 */
export function RenderSEOSchema({
  schemaData,
}: {
  schemaData: WithContext<any> | Graph;
}) {
  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </Head>
  );
}
